import { useEffect, useState } from "react";
import { http } from "../api";
import { PageHeader, EmptyState, showUndoToast } from "../shared";
import { Plus, Trash2, X, Sparkles, Eye, EyeOff, BookOpen, Heart } from "lucide-react";

const emptyPost = { title_en: "", title_bn: "", body_en: "", body_bn: "", cover_image: "", tags: [], published: false };

export default function AdviceCMS({ user }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [busy, setBusy] = useState(false);
  const [translating, setTranslating] = useState("");

  const load = async () => { const r = await http.get("/advice"); setPosts(r.data || []); };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const openNew = () => { setEditing({}); setForm(emptyPost); };
  const openEdit = (p) => { setEditing(p); setForm({
    title_en: p.title_en || "", title_bn: p.title_bn || "",
    body_en: p.body_en || "", body_bn: p.body_bn || "",
    cover_image: p.cover_image || "", tags: p.tags || [], published: !!p.published,
  }); };

  const save = async () => {
    if (!(form.title_en || form.title_bn) || !(form.body_en || form.body_bn)) {
      window.alert("Provide at least one language for title and body"); return;
    }
    setBusy(true);
    try {
      if (editing.post_id) {
        await http.patch(`/advice/${editing.post_id}`, form);
      } else {
        await http.post("/advice", form);
      }
      setEditing(null); load();
    } catch (e) { window.alert(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const translate = async (target, direction) => {
    if (!editing?.post_id) {
      // Save as draft first so translate endpoint has a post id
      const r = await http.post("/advice", { ...form, published: false });
      setEditing(r.data);
    }
    setTranslating(`${target}-${direction}`);
    try {
      const id = editing?.post_id || (await http.get("/advice")).data[0].post_id;
      const r = await http.post(`/advice/${id}/translate`, { target, direction });
      const key = direction === "en_to_bn" ? `${target}_bn` : `${target}_en`;
      setForm((f) => ({ ...f, [key]: r.data[key] }));
    } catch (e) { window.alert(e.response?.data?.detail || "Translate failed"); }
    finally { setTranslating(""); }
  };

  const remove = async (p) => {
    try {
      const r = await http.delete(`/advice/${p.post_id}`);
      load();
      showUndoToast({
        label: `"${p.title_en || p.title_bn}" moved to recycle bin`,
        hint: "Undo to bring it back.",
        onUndo: r.data?.trash_id ? async () => { await http.post(`/recycle-bin/${r.data.trash_id}/restore`); load(); } : null,
      });
    } catch (e) { window.alert(e.response?.data?.detail || "Delete failed"); }
  };

  if (!isAdmin) {
    return <><PageHeader eyebrow="ADVICE BLOG" title="Advice CMS" subtitle="Admin only" />
      <EmptyState icon={BookOpen} title="Admin access required" hint="Ask an admin to publish career-guidance content." /></>;
  }

  return (
    <>
      <PageHeader eyebrow="OUTREACH · PUBLIC CONTENT" title="Advice blog"
        subtitle="Publish bilingual career-guidance posts. English drafts get a → বাংলা AI-translate button (GPT-4o-mini) so mentors can go bilingual in one click."
        actions={<button className="primary" data-testid="ad-new" onClick={openNew}><Plus size={14} /> New post</button>} />

      <section className="panel">
        {posts.length === 0 ? (
          <EmptyState icon={BookOpen} title="No advice posts yet" hint="Publish your first post to help students and job-seekers." />
        ) : (
          <ul className="ad-list" data-testid="ad-list">
            {posts.map((p) => (
              <li key={p.post_id} className={`ad-row ${p.published ? "on" : ""}`} data-testid={`ad-row-${p.post_id}`}>
                <div className="ad-body">
                  <div className="ad-titles">
                    <b>{p.title_en || <em>— untitled EN —</em>}</b>
                    <span className="ad-bn">{p.title_bn || <em>— untitled BN —</em>}</span>
                  </div>
                  <div className="ad-meta">
                    {p.published ? <span className="ad-pub"><Eye size={11} /> Published</span> : <span className="ad-draft"><EyeOff size={11} /> Draft</span>}
                    <small>{p.author_name} · {new Date(p.updated_at || p.created_at).toLocaleString()}</small>
                    <small><Heart size={11} /> {p.reactions || 0}</small>
                    {(p.tags || []).slice(0, 3).map((t) => <span key={t} className="ad-tag">{t}</span>)}
                  </div>
                </div>
                <div className="ad-actions">
                  <button className="outline" data-testid={`ad-edit-${p.post_id}`} onClick={() => openEdit(p)}>Edit</button>
                  <button className="danger" data-testid={`ad-del-${p.post_id}`} onClick={() => remove(p)}><Trash2 size={12} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) setEditing(null); }}>
          <div className="ad-drawer" data-testid="ad-drawer">
            <div className="bo-drawer-head">
              <b>{editing.post_id ? "Edit post" : "New advice post"}</b>
              <button className="icon-btn" onClick={() => setEditing(null)}><X size={14} /></button>
            </div>

            <div className="ad-cols">
              <div>
                <label>Title (EN)
                  <input data-testid="ad-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} />
                </label>
                <label>Body (EN)
                  <textarea rows={6} data-testid="ad-body-en" value={form.body_en} onChange={(e) => setForm({ ...form, body_en: e.target.value })} />
                </label>
                <div className="ad-translate-row">
                  <button className="outline" data-testid="ad-tr-title-bn" disabled={!form.title_en || translating==='title-en_to_bn'} onClick={() => translate("title", "en_to_bn")}>
                    <Sparkles size={12} /> Title → বাংলা
                  </button>
                  <button className="outline" data-testid="ad-tr-body-bn" disabled={!form.body_en || translating==='body-en_to_bn'} onClick={() => translate("body", "en_to_bn")}>
                    <Sparkles size={12} /> Body → বাংলা
                  </button>
                </div>
              </div>
              <div>
                <label>Title (BN / বাংলা)
                  <input data-testid="ad-title-bn" value={form.title_bn} onChange={(e) => setForm({ ...form, title_bn: e.target.value })} />
                </label>
                <label>Body (BN / বাংলা)
                  <textarea rows={6} data-testid="ad-body-bn" value={form.body_bn} onChange={(e) => setForm({ ...form, body_bn: e.target.value })} />
                </label>
                <div className="ad-translate-row">
                  <button className="outline" data-testid="ad-tr-title-en" disabled={!form.title_bn || translating==='title-bn_to_en'} onClick={() => translate("title", "bn_to_en")}>
                    <Sparkles size={12} /> Title → English
                  </button>
                  <button className="outline" data-testid="ad-tr-body-en" disabled={!form.body_bn || translating==='body-bn_to_en'} onClick={() => translate("body", "bn_to_en")}>
                    <Sparkles size={12} /> Body → English
                  </button>
                </div>
              </div>
            </div>

            <div className="ad-tags-row">
              <label>Cover image URL
                <input data-testid="ad-cover" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} placeholder="https://…" />
              </label>
              <label>Tags (comma-separated)
                <input data-testid="ad-tags" value={(form.tags || []).join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="student, career, exam, entrepreneur" />
                <div className="ad-tag-picks" data-testid="ad-tag-picks">
                  {["student", "career", "exam", "entrepreneur"].map((tg) => {
                    const has = (form.tags || []).map((x) => x.toLowerCase()).includes(tg);
                    return (
                      <button
                        type="button"
                        key={tg}
                        className={`ad-tag-pick ${has ? "on" : ""}`}
                        data-testid={`ad-tag-pick-${tg}`}
                        onClick={() => {
                          const cur = form.tags || [];
                          if (has) {
                            setForm({ ...form, tags: cur.filter((x) => x.toLowerCase() !== tg) });
                          } else {
                            setForm({ ...form, tags: [...cur, tg] });
                          }
                        }}
                      >
                        {tg}
                      </button>
                    );
                  })}
                </div>
              </label>
              <label className="ad-pub-toggle">
                <input type="checkbox" data-testid="ad-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
                <span>Publish now</span>
              </label>
            </div>

            <div className="bo-drawer-actions">
              <button className="outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="primary" data-testid="ad-save" disabled={busy} onClick={save}>{busy ? "…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
